import { faDownload } from '@fortawesome/pro-light-svg-icons';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
    Banner,
    Breadcrumbs,
    FileUpload,
    ImportError,
    LoadingIndicator,
    ResidentialUnitImportEditor,
    ResidentialUnitImportRow,
} from '@ygtechnologies/bygr-frontend-component-library';
import { SimpleCard } from '@/components/Cards';
import { YesNoDialog } from '@/components/dialogs';
import { SimplePageTitle } from '@/components/layout/PageTitle';
import { BreadcrumbsProjectLink, projectRelativeLink } from '@/components/navigation';
import RouterPrompt from '@/components/RouterPrompt';
import { isEqual, keyBy, uniqBy } from 'lodash-es';
import React, { useCallback, useState } from 'react';
import { searchOrders } from '@/services/orders';
import { getProjectTags } from '@/services/project-tags';
import { getUnitVariableTypes } from '@/services/project-unit-variables';
import { getPricingFields, getResidentialUnitImportTemplate } from '@/services/projects';
import {
    getResidentialUnitsByProjectId,
    importResidentialUnits,
    parseResidentialUnitImportExcel,
} from '@/services/residential-units';
import { LinkingType, OptionGroupDto, Order, OrderStatus, PricingFieldValue, ProjectTag, ProjectTagType } from '@/types';
import { FileData } from '@ygtechnologies/rubus-frontend-utilities';
import { useAsyncState } from '@/utils/hooks';
import { getRoomDisplayName } from '@/utils/rooms';
import { ExtendedPricingFieldValue } from '../ResidentialUnitEdit/ResidentialUnitEditPricingFieldsConfirm';
import theme from './ResidentialUnitImport.module.scss';
import ResidentialUnitImportChangesModal from './ResidentialUnitImportChangesModal';
import { Button, ButtonBar, ConfirmButton } from '@ygtechnologies/rubus-ui';
import { useRooms } from '@/services/rooms';
import { useHistory } from 'react-router';
import { searchOptionGroups } from '@/services/options/option-groups';

interface ResidentialUnitImportProps {
    projectId: number;
}

export type StepData = StepBuyerOptionGroup | StepOrder | StepTagBuyerOption | StepTagOrder;

interface StepCommonData {
    residentialUnitId: number;
    residentialUnitName: string;
}

interface StepBuyerOptionGroup extends StepCommonData {
    data: { buyerOptionsGroups: OptionGroupDto[]; changedPricingFields: ExtendedPricingFieldValue[] };
    type: 'BuyerOption';
}
interface StepOrder extends StepCommonData {
    data: { orders: Order[]; changedPricingFields: ExtendedPricingFieldValue[] };
    type: 'Order';
}

interface StepTagBuyerOption extends StepCommonData {
    data: { removedTags: ProjectTag[]; buyerOptionsGroups: OptionGroupDto[] };
    type: 'TagBuyerOption';
}

interface StepTagOrder extends StepCommonData {
    data: { orders: Order[] };
    type: 'TagOrder';
}

export interface BuyerOptionGroupsToUnlinkData {
    residentialUnitId: number;
    buyerOptionGroupToUnlinkIds: number[];
}

const ResidentialUnitImport: React.FC<ResidentialUnitImportProps> = ({ projectId }) => {
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [parseFailed, setParseFailed] = useState(false);
    const [parsedData, setParsedData] = useState<ResidentialUnitImportRow[] | null>(null);
    const [pricingFields] = useAsyncState(() => getPricingFields(projectId), [projectId], null);
    const [tags] = useAsyncState(() => getProjectTags(projectId, ProjectTagType.Room), [projectId], null);
    const { data: rooms, isLoading: roomsLoading } = useRooms(projectId);
    const [currentResidentialUnits] = useAsyncState(() => getResidentialUnitsByProjectId(projectId), [projectId], null);
    const [unlinkData, setUnlinkData] = useState<BuyerOptionGroupsToUnlinkData[]>([]);
    const [customFieldTypes] = useAsyncState(() => getUnitVariableTypes(projectId), [], null);
    const [residentialUnitImportPricingValueChangeModalIsOpen, setResidentialUnitImportPricingValueChangeModalIsOpen] =
        useState(false);

    const [modalSteps, setModalSteps] = useState<StepData[]>([]);

    const [errors, setErrors] = useState<ImportError[]>([]);
    const [allowFaultyValues, setAllowFaultyValues] = useState(false);
    const [showSaveAnywaysDialog, setShowSaveAnywaysDialog] = useState(false);
    const [showClearErrorsDialog, setShowClearErrorsDialog] = useState(false);

    const history = useHistory();
    // const handlePaste = async (data) => {
    //     setLoading(true);
    //     setParseFailed(false);

    //     try {
    //         const result = await parseResidentialUnitImportText(projectId, data);
    //         setParsedData(result);
    //     } catch (e: any) {
    //         setParsedData(null);
    //         setParseFailed(true);
    //     }

    //     setLoading(false);
    // };
    const getBuyerOptionGroupToUnlinkIds = useCallback(
        (unitId: number) => unlinkData.find((x) => x.residentialUnitId === unitId)?.buyerOptionGroupToUnlinkIds ?? null,
        [unlinkData]
    );

    const handleDrop = async (file) => {
        setLoading(true);
        setParseFailed(false);

        try {
            const result = await parseResidentialUnitImportExcel(projectId, new FileData(file[0]));
            setParsedData(result);
        } catch (e: any) {
            setParsedData(null);
            setParseFailed(true);
        }

        setLoading(false);
    };

    const createRemovedTagsStep = async (unit: ResidentialUnitImportRow, removedTags: ProjectTag[]) => {
        if (removedTags && removedTags.length > 0 && unit.id) {
            // Removing a tag only cleans up directly-linked option groups. Room-linked groups stay
            // linked (the configurator manages those), and searchOptionGroups also returns groups
            // that merely share a tag with the unit, so keep only Direct groups linked to THIS unit.
            const groups = (
                await searchOptionGroups({
                    projectId,
                    tagIds: removedTags.map((t) => t.id),
                    unitIds: [unit.id],
                })
            ).filter((g) => g.linkType === LinkingType.Direct && g.linkedUnits.some((u) => u.residentialUnitId === unit.id));

            setUnlinkData((old) => [
                ...old,
                { residentialUnitId: unit.id!, buyerOptionGroupToUnlinkIds: groups.map((g) => g.id) },
            ]);
            if (groups.length > 0) {
                // searchOrders treats an empty sourceOptionGroupIds as "no filter", so only query
                // when there are groups to unlink.
                const orders = await searchOrders({
                    residentialUnitIds: [unit.id!],
                    sourceOptionGroupIds: groups.map((g) => g.id),
                    status: [OrderStatus.ReadyToStart],
                });

                const tagBuyerOptionStep = {
                    residentialUnitId: unit.id,
                    residentialUnitName: unit.residentialUnitName,
                    type: 'TagBuyerOption',
                    data: {
                        removedTags,
                        buyerOptionsGroups: groups,
                    },
                } as StepData;

                if (orders.length > 0) {
                    return [
                        tagBuyerOptionStep,
                        {
                            residentialUnitId: unit.id,
                            residentialUnitName: unit.residentialUnitName,
                            type: 'TagOrder',
                            data: {
                                orders,
                            },
                        } as StepData,
                    ];
                }

                return [tagBuyerOptionStep];
            }
        }
        return [];
    };

    const createPriceFieldStep = async (unit: ResidentialUnitImportRow, oldPricingFields: PricingFieldValue[]) => {
        const changedPricingFields: ExtendedPricingFieldValue[] = [];

        oldPricingFields.forEach((oldPricingFiled) => {
            const importUnitPricingField = unit.pricingFields.find(
                (y) => y.name.toLowerCase() === oldPricingFiled.name.toLowerCase()
            );
            if (importUnitPricingField?.value !== oldPricingFiled.value) {
                changedPricingFields.push({
                    value: unit.pricingFields.find((y) => y.name === oldPricingFiled.name)!.value,
                    oldValue: oldPricingFiled.value!,
                    pricingFieldId: oldPricingFiled.pricingFieldId,
                    name: oldPricingFiled.name,
                });
            }
        });

        const groups = await searchOptionGroups({
            projectId,
            pricingFieldIds: changedPricingFields!.map((pf) => pf.pricingFieldId),
            unitIds: [unit.id!],
        });

        if (groups.length > 0) {
            const orders = await searchOrders({
                residentialUnitIds: [unit.id!],
                sourceOptionGroupIds: groups.map((g) => g.id),
                status: [OrderStatus.ReadyToStart],
            });

            const groupStep: StepData = {
                type: 'BuyerOption',
                data: {
                    buyerOptionsGroups: groups,
                    changedPricingFields,
                },
                residentialUnitId: unit.id!,
                residentialUnitName: unit.residentialUnitName,
            };

            if (orders.length > 0) {
                return [
                    groupStep,
                    {
                        data: {
                            changedPricingFields,
                            orders,
                        },
                        type: 'Order',
                        residentialUnitId: unit.id!,
                        residentialUnitName: unit.residentialUnitName,
                    } as StepData,
                ];
            }
            return [groupStep];
        }
        return [];
    };

    const handleDataChange = (newData: ResidentialUnitImportRow[]) => setParsedData(newData);

    const handleDiscardClick = () => setParsedData(null);

    const save = async () => {
        setSaving(true);
        if (!parsedData) return;
        try {
            await importResidentialUnits(projectId, parsedData, unlinkData);
            history.push(projectRelativeLink('/residential-units', projectId));
        } finally {
            setSaving(false);
        }
    };

    const handleSaveClick = async () => {
        if (!parsedData) return;
        setSaving(true);
        let steps: StepData[] = [];

        const unitsDictionary = keyBy(currentResidentialUnits, (x) => x.id);
        const unitsWithPricingFieldsChanges = parsedData.filter(
            (x) =>
                x.id !== null &&
                !isEqual(
                    x.pricingFields,
                    unitsDictionary[x.id].pricingFields.map((pf) => ({ name: pf.name, value: pf.value }))
                )
        );

        for (const unit of unitsWithPricingFieldsChanges) {
            steps = steps.concat(await createPriceFieldStep(unit, unitsDictionary[unit.id!].pricingFields));
        }

        // Only old-style Room tags drive option-group cleanup (matches the single-unit edit flow).
        // The unit's projectTags don't carry their type, so identify Room tags by id via the loaded
        // Room tag list (`tags`). NewRoom tags are managed by the configurator, not unlinked here.
        const roomTagIds = new Set((tags ?? []).map((t) => t.id));

        for (const parsedUnit of parsedData) {
            if (parsedUnit.id) {
                const currentUnit = currentResidentialUnits!.find((x) => x.id === parsedUnit.id);
                const removedTags = currentUnit!.projectTags.filter(
                    (pt) => roomTagIds.has(pt.id) && !parsedUnit.tags.includes(pt.label)
                );
                if (removedTags.length > 0) steps = steps.concat(await createRemovedTagsStep(parsedUnit, removedTags));
            }
        }
        if (steps.length !== 0) {
            setModalSteps(steps);
            setSaving(false);
            setResidentialUnitImportPricingValueChangeModalIsOpen(true);
        } else save();
    };

    const renderBody = () => {
        if (!pricingFields || !tags || loading || !customFieldTypes || roomsLoading) return <LoadingIndicator />;

        if (parsedData) {
            const uniqueErrorMessages = uniqBy(errors, 'field');

            return (
                <div>
                    <RouterPrompt when={!!parsedData && !saving} />
                    {errors.length > 0 && (
                        <Banner type="danger" icon={null} className={theme.errorBanner}>
                            <h4>
                                <Trans>Invalid value types</Trans>
                            </h4>
                            {uniqueErrorMessages.map((error) => (
                                <div key={error.field}>
                                    <b>
                                        {errors.filter((e) => e.field === error.field).length} x {error.field}:
                                    </b>{' '}
                                    {error.error}
                                </div>
                            ))}
                        </Banner>
                    )}

                    <ResidentialUnitImportEditor
                        data={parsedData}
                        onChange={handleDataChange}
                        pricingFields={pricingFields}
                        availableTags={tags.concat(
                            rooms!.map((x) => ({
                                id: x.id!,
                                label: getRoomDisplayName(x.name, x.labelNote),
                                labelNote: x.labelNote,
                                sortOrder: x.sortOrder,
                                type: ProjectTagType.NewRoom,
                            }))
                        )}
                        customFieldTypes={customFieldTypes}
                        errors={errors}
                        setErrors={setErrors}
                    />

                    <ButtonBar>
                        <ButtonBar.Right>
                            <ConfirmButton
                                disabled={saving}
                                variant="tertiary"
                                onConfirm={handleDiscardClick}
                                dialogTitle={<Trans>Discard</Trans>}
                                dialogBody={<Trans>This will discard all data. Are you sure?</Trans>}>
                                <Trans>Discard</Trans>
                            </ConfirmButton>
                            {errors.length > 0 && (
                                <>
                                    <Button variant="tertiary" onClick={() => setShowClearErrorsDialog(true)}>
                                        <Trans>Clear errors</Trans>
                                    </Button>
                                    <Button variant="brand" onClick={() => setShowSaveAnywaysDialog(true)}>
                                        <Trans>Save anyways</Trans>
                                    </Button>
                                </>
                            )}
                            {errors.length === 0 && (
                                <Button
                                    disabled={errors.length > 0 && !allowFaultyValues}
                                    variant="primary"
                                    onClick={handleSaveClick}
                                    loading={saving}>
                                    <Trans>Save</Trans>
                                </Button>
                            )}
                        </ButtonBar.Right>
                    </ButtonBar>
                </div>
            );
        }

        return (
            <SimpleCard className={theme.uploadCard}>
                {parseFailed && (
                    <div className={theme.errorMessage}>
                        <Trans>
                            Error parsing data, please check that the copied data is in the correct format and try again.
                        </Trans>
                    </div>
                )}
                <ButtonBar>
                    <Button
                        as="a"
                        href={getResidentialUnitImportTemplate(projectId, false)}
                        icon={faDownload}
                        variant="tertiary">
                        <Trans>Download import template</Trans>
                    </Button>

                    <Button
                        as="a"
                        href={getResidentialUnitImportTemplate(projectId, true)}
                        icon={faDownload}
                        variant="tertiary">
                        <Trans>Download update template</Trans>
                    </Button>

                    <Button variant="tertiary" onClick={(e) => setParsedData([])}>
                        <Trans>Input units manually</Trans>
                    </Button>
                </ButtonBar>

                <hr />

                <div>
                    <FileUpload single onDrop={handleDrop} />
                </div>
            </SimpleCard>
        );
    };

    return (
        <>
            <div className={theme.container}>
                <SimplePageTitle title={t`Import units`} />

                <Breadcrumbs>
                    <BreadcrumbsProjectLink to="/residential-units">
                        <Trans>All units</Trans>
                    </BreadcrumbsProjectLink>

                    <Breadcrumbs.Item>
                        <Trans>Import units</Trans>
                    </Breadcrumbs.Item>
                </Breadcrumbs>

                {renderBody()}
            </div>

            {residentialUnitImportPricingValueChangeModalIsOpen && (
                <ResidentialUnitImportChangesModal
                    data={modalSteps}
                    isOpen={residentialUnitImportPricingValueChangeModalIsOpen}
                    onSubmit={save}
                    onCancel={() => setResidentialUnitImportPricingValueChangeModalIsOpen(false)}
                    getBuyerOptionGroupToUnlinkIds={getBuyerOptionGroupToUnlinkIds}
                    onBuyerOptionGroupToUnlinkIdsChange={(unitId, unlinkIds) =>
                        setUnlinkData((old) =>
                            old.map((x) =>
                                x.residentialUnitId === unitId ? { ...x, buyerOptionGroupToUnlinkIds: unlinkIds } : x
                            )
                        )
                    }
                />
            )}

            <YesNoDialog
                title={<Trans>Save with errors</Trans>}
                body={
                    <Trans>
                        Are you sure you want to save the data with validation errors? This may cause unexpected behavior in
                        the system.
                    </Trans>
                }
                show={showSaveAnywaysDialog}
                onAccept={() => {
                    setAllowFaultyValues(true);
                    handleSaveClick();
                    setShowSaveAnywaysDialog(false);
                }}
                onClose={() => setShowSaveAnywaysDialog(false)}
                confirmButtonColor="danger"
                confirmButtonText={<Trans>Save anyways</Trans>}
                cancelButtonText={<Trans>Cancel</Trans>}
            />

            <YesNoDialog
                title={<Trans>Clear all errors</Trans>}
                body={
                    <Trans>
                        Are you sure you want to clear all validation errors? This will set all invalid field values to
                        empty.
                    </Trans>
                }
                show={showClearErrorsDialog}
                onAccept={() => {
                    if (!parsedData) return;

                    const updatedData = parsedData.map((unit, unitIndex) => {
                        const unitErrors = errors.filter((error) => error.rowNumber === unitIndex + 1);

                        if (unitErrors.length === 0) return unit;

                        const updatedUnit = { ...unit };

                        unitErrors.forEach((error) => {
                            const fieldName = error.field;

                            // Handle pricing fields
                            if (updatedUnit.pricingFields?.some((pf) => pf.name === fieldName)) {
                                updatedUnit.pricingFields = updatedUnit.pricingFields.map((pf) =>
                                    pf.name === fieldName ? { ...pf, value: null } : pf
                                );
                            }

                            // Handle custom fields
                            if (updatedUnit.customFields?.some((cf) => cf.name === fieldName)) {
                                updatedUnit.customFields = updatedUnit.customFields.map((cf) =>
                                    cf.name === fieldName ? { ...cf, value: '' } : cf
                                );
                            }

                            // Handle other direct properties if needed
                            if (fieldName in updatedUnit && fieldName !== 'pricingFields' && fieldName !== 'customFields') {
                                (updatedUnit as any)[fieldName] = '';
                            }
                        });

                        return updatedUnit;
                    });

                    setParsedData(updatedData);
                    setErrors([]);
                    setShowClearErrorsDialog(false);
                }}
                onClose={() => setShowClearErrorsDialog(false)}
                confirmButtonColor="primary"
                confirmButtonText={<Trans>Clear errors</Trans>}
                cancelButtonText={<Trans>Cancel</Trans>}
            />
        </>
    );
};

export default ResidentialUnitImport;
